import { TestFailuresResponse } from "~/components/build/Show/lib/queries/testFailures";

export type TestFailure = TestFailuresResponse["failures"][number];

export type FailureMarker = {
  failure: TestFailure;
  startLine: number;
  endLine: number;
  groupId?: string;
};

type LogLine = { number: number; textContent: string };
type LogGroup = { id: string; lines: LogLine[] };

/**
 * Count the total number of lines (including empty) in the failure_expanded
 * structure. Used to size the marker range in the log output.
 */
export function expandedLineCount(failureExpanded: TestFailure["failureExpanded"]): number {
  if (!failureExpanded) {
    return 0;
  }

  let count = 0;
  for (const section of failureExpanded) {
    if (section.expanded) {
      count += section.expanded.length;
    }
    if (section.backtrace) {
      count += section.backtrace.length;
    }
    // Account for the blank separator line between expanded and backtrace
    // that appears in the log output but isn't in either array
    if (section.expanded?.length && section.backtrace?.length) {
      count += 1;
    }
  }
  return count;
}

/**
 * Matches each test failure to a range of lines in the log output, producing
 * gutter markers that highlight where failures occurred.
 *
 * **Strategy**: For every failure, search backward through the flat list of log
 * lines for the first unclaimed line whose `textContent` contains the failure's
 * `failureReason`. Once found, extend the marker range forward by the number of
 * lines in the `failureExpanded` structure (expanded + backtrace + separators).
 *
 * **Why search backward?** When a test is retried, the same `failureReason`
 * text appears multiple times in the log. Searching from the end ensures each
 * failure matches the *latest* attempt first, which is the most relevant one.
 *
 * **`usedIndices`**: A set of already-claimed line indices prevents two failures
 * with identical `failureReason` text from anchoring on the same log line. The
 * first failure processed claims the last occurrence; the next failure then
 * falls back to the second-to-last occurrence, and so on.
 *
 * **Output ordering**: Results are sorted ascending by `startLine` so that
 * `markers[0]` is always the earliest failure in the log. A final deduplication
 * pass removes any markers that share the same `startLine`, which can happen
 * when more failures exist than matching log lines.
 */
export function matchFailureMarkers(failures: TestFailure[], groups: LogGroup[]): FailureMarker[] {
  // Build a flat array of all lines across all groups
  const allLines: LogLine[] = [];
  for (const group of groups) {
    for (const line of group.lines) {
      allLines.push(line);
    }
  }

  if (!failures.length || !allLines.length) {
    return [];
  }

  // Build a line-number → groupId lookup and track the last line of each group
  const lineToGroup = new Map<number, string>();
  const groupLastLine = new Map<string, number>();
  for (const group of groups) {
    for (const line of group.lines) {
      lineToGroup.set(line.number, group.id);
    }
    if (group.lines.length > 0) {
      groupLastLine.set(group.id, group.lines[group.lines.length - 1].number);
    }
  }

  const result: FailureMarker[] = [];

  // Track which line indices have already been claimed so that multiple
  // failures with the same failureReason each match a distinct log line.
  const usedIndices = new Set<number>();

  for (const failure of failures) {
    if (!failure.failureReason) {
      continue;
    }

    // Search backwards for the last unclaimed line matching this failure.
    let anchorIdx = -1;
    for (let idx = allLines.length - 1; idx >= 0; idx--) {
      if (!usedIndices.has(idx) && allLines[idx].textContent.includes(failure.failureReason)) {
        anchorIdx = idx;
        break;
      }
    }

    if (anchorIdx === -1) {
      continue;
    }

    usedIndices.add(anchorIdx);

    const anchorLine = allLines[anchorIdx].number;
    let endLine = anchorLine;

    // Extend the range by the total number of expanded/backtrace lines
    // (including empty lines, which appear as blank rows in the log output).
    // Cap to the last line of the anchor's group so the failure gutter
    // doesn't bleed into the next group's lines.
    const expandedCount = expandedLineCount(failure.failureExpanded);
    if (expandedCount > 0) {
      const lastIdx = Math.min(anchorIdx + expandedCount, allLines.length - 1);
      endLine = allLines[lastIdx].number;

      const groupId = lineToGroup.get(anchorLine);
      if (groupId) {
        const groupEnd = groupLastLine.get(groupId);
        if (groupEnd !== undefined) {
          endLine = Math.min(endLine, groupEnd);
        }
      }
    }

    result.push({
      failure,
      startLine: anchorLine,
      endLine,
      groupId: lineToGroup.get(anchorLine),
    });
  }

  // Sort by position in the log so markers[0] is always the earliest failure
  result.sort((aa, bb) => aa.startLine - bb.startLine);

  // Deduplicate markers that share the same anchor line (e.g. retried tests)
  const seen = new Set<number>();
  return result.filter((marker) => {
    if (seen.has(marker.startLine)) {
      return false;
    }
    seen.add(marker.startLine);
    return true;
  });
}
